'use client';

import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useAppColors } from '@/styles/colors';
import {
  defaultTreasurySeries,
  filterTreasuryPeriod,
  formatTreasuryDate,
  periodDays,
  treasuryPeriodContext,
  type TreasuryQuotePoint,
  type TreasurySeries,
} from './treasury-view';

export type TreasuryChartSeries = { key: TreasurySeries; label: string; axis: 'left' | 'right' };
type Props = {
  title: string;
  points: TreasuryQuotePoint[];
  series:
    | [TreasuryChartSeries, TreasuryChartSeries]
    | [TreasuryChartSeries, TreasuryChartSeries, TreasuryChartSeries, TreasuryChartSeries];
  defaultEnabled: TreasurySeries[];
  tooltipSeries?: TreasurySeries[];
  statsSeries?: TreasurySeries;
  source?: string;
};

const periods = Object.keys(periodDays);
const numberFormat = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const currencyFormat = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatValue = (value: number | null, series: TreasurySeries) =>
  value === null || !Number.isFinite(value)
    ? '—'
    : series.endsWith('Pu')
      ? currencyFormat.format(value)
      : `${numberFormat.format(value)}% a.a.`;

export function TreasuryQuoteChart({
  title,
  points,
  series,
  defaultEnabled,
  tooltipSeries = series.map((item) => item.key),
  statsSeries,
  source,
}: Props) {
  const colors = useAppColors();
  const [period, setPeriod] = useState('1A');
  const [enabled, setEnabled] = useState<Record<TreasurySeries, boolean>>(() =>
    defaultTreasurySeries(
      series.map((item) => item.key),
      defaultEnabled,
    ),
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const selected = useMemo(() => filterTreasuryPeriod(points, period), [points, period]);
  const activeSeries = series.filter((item) => enabled[item.key]);
  const rows = selected.filter((point) => activeSeries.some((item) => Number.isFinite(point[item.key] ?? NaN)));
  const context = treasuryPeriodContext(rows, period);
  const width = 1000;
  const height = 380;
  const left = 84;
  const right = 916;
  const top = 42;
  const bottom = 318;
  const chartWidth = right - left;
  const chartHeight = bottom - top;
  const axisValues = (axis: 'left' | 'right') =>
    rows.flatMap((point) =>
      activeSeries
        .filter((item) => item.axis === axis)
        .map((item) => point[item.key])
        .filter((value): value is number => value !== null && Number.isFinite(value)),
    );
  const bounds = (values: number[]) => {
    if (!values.length) return [0, 1] as const;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = Math.max((max - min) * 0.08, Math.abs(max) * 0.02, 0.01);
    return [min - pad, max + pad] as const;
  };
  const leftBounds = bounds(axisValues('left'));
  const rightBounds = bounds(axisValues('right'));
  const xFor = (index: number) =>
    rows.length === 1 ? (left + right) / 2 : left + (index / (rows.length - 1)) * chartWidth;
  const yFor = (value: number, axis: 'left' | 'right') => {
    const [min, max] = axis === 'left' ? leftBounds : rightBounds;
    return top + chartHeight - ((value - min) / (max - min || 1)) * chartHeight;
  };
  const pathFor = (item: TreasuryChartSeries) =>
    rows
      .map((point, index) => {
        const value = point[item.key];
        return value === null || !Number.isFinite(value)
          ? null
          : `${index ? 'L' : 'M'}${xFor(index).toFixed(1)} ${yFor(value, item.axis).toFixed(1)}`;
      })
      .filter(Boolean)
      .join(' ');
  const hovered = hoveredIndex === null ? null : rows[hoveredIndex];
  const palette = colors.modules.priceHistorySeries;
  const empty = !activeSeries.length || !rows.length;
  const chartLabel = `${title}: ${activeSeries.length ? activeSeries.map((item) => item.label).join(', ') : 'nenhuma série visível'}, ${context.startDate} a ${context.endDate}`;
  const chartDescription = [
    activeSeries.some((item) => item.axis === 'left') ? 'taxa no eixo esquerdo' : null,
    activeSeries.some((item) => item.axis === 'right') ? 'preço unitário no eixo direito' : null,
  ]
    .filter(Boolean)
    .join(' e ');

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 }, width: '100%', minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        spacing={1.5}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box>
          <Typography variant='h6' fontWeight={700}>
            {title}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {context.label} · {context.startDate} — {context.endDate}
          </Typography>
        </Box>
        <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap role='group' aria-label='Selecionar período'>
          {periods.map((item) => (
            <Button
              key={item}
              size='small'
              variant={period === item ? 'contained' : 'outlined'}
              aria-pressed={period === item}
              onClick={() => setPeriod(item)}
            >
              {item}
            </Button>
          ))}
        </Stack>
      </Stack>
      <Stack
        direction='row'
        spacing={1.5}
        flexWrap='wrap'
        useFlexGap
        sx={{ mt: 1.5, mb: 1 }}
        role='group'
        aria-label='Alternar séries do gráfico'
      >
        {series.map((item, index) => (
          <Box
            component='button'
            key={item.key}
            type='button'
            aria-label={`${enabled[item.key] ? 'Ocultar' : 'Mostrar'} ${item.label}`}
            aria-pressed={enabled[item.key]}
            onClick={() => setEnabled((state) => ({ ...state, [item.key]: !state[item.key] }))}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              border: 0,
              background: 'transparent',
              color: 'inherit',
              p: 0.5,
              cursor: 'pointer',
              borderRadius: 1,
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }}
          >
            <span aria-hidden='true' style={{ width: 16, borderTop: `3px solid ${palette[index % palette.length]}` }} />
            <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
        <Typography variant='caption' color='text.secondary' sx={{ alignSelf: 'center' }}>
          {activeSeries.some((item) => item.axis === 'left') ? 'Taxa (a.a.) · eixo esquerdo' : ''}
          {activeSeries.some((item) => item.axis === 'left') && activeSeries.some((item) => item.axis === 'right')
            ? ' · '
            : ''}
          {activeSeries.some((item) => item.axis === 'right') ? 'PU · eixo direito' : ''}
        </Typography>
      </Stack>
      {empty ? (
        <Typography color='text.secondary' sx={{ py: 8, textAlign: 'center' }}>
          {activeSeries.length
            ? 'Não há dados para este período.'
            : 'Selecione ao menos uma série para visualizar o gráfico.'}
        </Typography>
      ) : (
        <>
          <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              width='100%'
              role='img'
              aria-label={chartLabel}
              aria-describedby='treasury-chart-description'
              tabIndex={0}
              onMouseLeave={() => setHoveredIndex(null)}
              onMouseMove={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                if (!rect.width) return;
                const relative = Math.max(left, Math.min(right, ((event.clientX - rect.left) / rect.width) * width));
                setHoveredIndex(
                  Math.max(
                    0,
                    Math.min(rows.length - 1, Math.round(((relative - left) / chartWidth) * (rows.length - 1))),
                  ),
                );
              }}
              onKeyDown={(event) => {
                const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
                if (delta || event.key === 'Home' || event.key === 'End') {
                  event.preventDefault();
                  setHoveredIndex(
                    event.key === 'Home'
                      ? 0
                      : event.key === 'End'
                        ? rows.length - 1
                        : Math.max(0, Math.min(rows.length - 1, (hoveredIndex ?? 0) + delta)),
                  );
                }
              }}
            >
              <title>{chartLabel}</title>
              <desc id='treasury-chart-description'>
                Gráfico com {chartDescription || 'nenhuma série visível'}. Use as setas para consultar os valores.
              </desc>
              {Array.from({ length: 5 }).map((_, index) => {
                const ratio = index / 4;
                const y = top + ratio * chartHeight;
                const leftValue = leftBounds[1] - ratio * (leftBounds[1] - leftBounds[0]);
                const rightValue = rightBounds[1] - ratio * (rightBounds[1] - rightBounds[0]);
                return (
                  <g key={index}>
                    <line x1={left} x2={right} y1={y} y2={y} stroke='hsl(var(--border))' strokeDasharray='4 4' />
                    {activeSeries.some((item) => item.axis === 'left') ? (
                      <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                        {numberFormat.format(leftValue)}%
                      </text>
                    ) : null}
                    {activeSeries.some((item) => item.axis === 'right') ? (
                      <text x={right + 8} y={y + 4} fontSize='12' fill='hsl(var(--muted-foreground))'>
                        {currencyFormat.format(rightValue)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
              {activeSeries.some((item) => item.axis === 'left') ? (
                <text x={left} y={18} fontSize='12' fill='hsl(var(--muted-foreground))'>
                  Taxa (a.a.)
                </text>
              ) : null}
              {activeSeries.some((item) => item.axis === 'right') ? (
                <text x={right} y={18} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                  PU
                </text>
              ) : null}
              {series.map((item, index) =>
                enabled[item.key] ? (
                  <path
                    key={item.key}
                    d={pathFor(item)}
                    fill='none'
                    stroke={palette[index % palette.length]}
                    strokeWidth='2.5'
                  />
                ) : null,
              )}
              {hovered ? (
                <line
                  x1={xFor(hoveredIndex ?? 0)}
                  x2={xFor(hoveredIndex ?? 0)}
                  y1={top}
                  y2={bottom}
                  stroke='hsl(var(--muted-foreground) / 0.65)'
                  strokeDasharray='4 4'
                />
              ) : null}
              <text x={left} y={height - 12} fontSize='12' fill='hsl(var(--muted-foreground))'>
                {context.startDate}
              </text>
              <text x={right} y={height - 12} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {context.endDate}
              </text>
            </svg>
          </Box>
          {hovered ? (
            <Box role='tooltip' sx={{ mt: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant='caption' color='text.secondary'>
                {formatTreasuryDate(hovered.date)}
              </Typography>
              {tooltipSeries.map((key) => (
                <Stack key={key} direction='row' justifyContent='space-between' spacing={2}>
                  <Typography variant='caption' color='text.secondary'>
                    {series.find((item) => item.key === key)?.label ?? key}
                  </Typography>
                  <Typography variant='caption' fontWeight={700}>
                    {formatValue(hovered[key], key)}
                  </Typography>
                </Stack>
              ))}
            </Box>
          ) : null}
        </>
      )}
      {statsSeries ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
          Série de referência: {series.find((item) => item.key === statsSeries)?.label ?? statsSeries}
        </Typography>
      ) : null}
      {source ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
          {source}
        </Typography>
      ) : null}
    </Paper>
  );
}
